import { styled } from '@mui/material/styles';
import { TableCell, TableContainer, Theme, Paper, Table, Box, Typography, Button } from '@mui/material';

// Table container styled components from Table.styles.tsx
export const TableWrapper = styled(Box)(({ theme }) => ({
  padding: '24px',
  background: 'white',
  borderRadius: '6px',
  boxShadow: '0px 0px 4px 0px rgba(0,0,0,0.08)',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    padding: '16px',
    gap: '16px',
  },
}));

export const TableTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.375rem',
  textTransform: 'uppercase',
  fontWeight: 700,
  lineHeight: '1.56',
  color: 'rgb(7, 49, 66)',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.25rem',
  },
}));

export const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
  width: '100%',
}));

export const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  width: '100%',
  position: 'relative',
  [theme.breakpoints.down('sm')]: {
    // Mobile specific styles if needed
  },
}));

export const StyledTable = styled(Table)(({ theme }) => ({
  tableLayout: 'auto',
  width: '100%',
  borderSpacing: '4px',
  borderCollapse: 'separate',
}));

export const HeaderCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: '#E7EFF5',
  color: 'rgba(0, 0, 0, 0.8)',
  fontWeight: 700,
  textAlign: 'center',
  fontSize: '12px',
  fontFamily: "'Lato', sans-serif",
  border: '1px solid #d5e4f0',
  borderRadius: '4px',
  height: '24px',
  padding: '0px !important',
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
    padding: '4px 6px !important',
    height: '28px',
  },
}));

export const SubHeaderCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: '#E7EFF5',
  color: 'rgba(0, 0, 0, 0.8)',
  fontWeight: 700,
  fontSize: '12px',
  fontFamily: "'Lato', sans-serif",
  textAlign: 'center',
  border: '1px solid #d5e4f0',
  borderRadius: '4px',
  height: '24px',
  padding: '0px !important',
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
    padding: '0px !important',
    height: '28px',
  },
}));

export const DataCell = styled(TableCell)(({ theme }) => ({
  fontSize: '12px',
  fontFamily: "'Lato', sans-serif",
  fontWeight: 400,
  textAlign: 'center',
  border: '1px solid #e0e0e0',
  borderRadius: '4px',
  padding: '0px !important',
  background: 'transparent',
  color: 'rgba(0, 0, 0, 0.8)',
  height: '16px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '11px',
    padding: '0px !important',
    height: '28px',
  },
}));

export const StatusCell = styled(DataCell)<{ status?: string; fallbackBackgroundColor?: string }>(({ status, fallbackBackgroundColor, theme }) => {
  let backgroundColor = fallbackBackgroundColor || 'transparent';
  let color = 'rgba(0, 0, 0, 0.8)';
  let fontWeight = 400;

  switch(status) {
    case 'A': // Accepted
      backgroundColor = '#2FF035';
      color = 'black';
      fontWeight = 700;
      break;
    case 'D': // Downloaded
      backgroundColor = '#FDE163';
      color = 'black';
      fontWeight = 700;
      break;
    case 'U': // Uploaded
      backgroundColor = '#FBFF09';
      color = 'black';
      fontWeight = 700;
      break;
    case 'R': // Marked for removal
      backgroundColor = 'rgba(244, 67, 54, 0.15)';
      color = 'black';
      fontWeight = 700;
      break;
    default:
      // Use specific color for empty status cells (null, undefined, empty string), fallback for others
      backgroundColor = (!status || status === '' || status.trim() === '') ? '#F58E9F' : (fallbackBackgroundColor || 'transparent');
      color = 'black';
      fontWeight = 400;
  }

  return {
    backgroundColor,
    fontWeight,
    color,
    borderRadius: '4px',
    border: '1px solid #e0e0e0',
    fontSize: '12px',
    fontFamily: "'Lato', sans-serif",
    padding: '0px !important',
    height: '16px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '11px',
      padding: '0px !important',
      height: '28px',
    },
  };
});

export const QuarterFirstCell = styled(DataCell)(({ theme }) => ({
  fontSize: '12px',
  fontFamily: "'Lato', sans-serif",
  fontWeight: 400,
  textAlign: 'center',
  border: '1px solid #e0e0e0',
  borderRadius: '4px',
  padding: '0px !important',
  background: 'transparent',
  color: 'rgba(0, 0, 0, 0.8)',
  height: '24px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '11px',
    padding: '0px !important',
    height: '28px',
  },
}));

// Closed row styled components
export const ClosedDataCell = styled(DataCell)(({ theme }) => ({
  backgroundColor: 'rgba(50, 205, 50, 0.15)',
  fontWeight: 700,
  fontSize: '12px',
  fontFamily: "'Lato', sans-serif",
  textAlign: 'center',
  border: '1px solid #c8e6c9',
  borderRadius: '4px',
  padding: '0px !important',
  height: '20px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '11px',
    padding: '0px !important',
    height: '20px',
  },
}));

export const ClosedStatusCell = styled(ClosedDataCell)<{ status?: string; fallbackBackgroundColor?: string }>(({ status, fallbackBackgroundColor, theme }) => {
  // For closed rows, we want to maintain the green styling but with slightly different shades for different statuses
  let backgroundColor = 'rgba(50, 205, 50, 0.15)';
  let color = 'black';

  switch(status) {
    case 'A': // Accepted
      backgroundColor = 'rgba(50, 205, 50, 0.25)';
      color = 'black';
      break;
    case 'D': // Downloaded
      backgroundColor = 'rgba(50, 205, 50, 0.2)';
      color = 'black';
      break;
    case 'U': // Uploaded
      backgroundColor = 'rgba(50, 205, 50, 0.2)';
      color = 'black';
      break;
    case 'R': // Marked for removal
      backgroundColor = 'rgba(50, 205, 50, 0.2)';
      color = 'black';
      break;
    default:
      backgroundColor = 'rgba(50, 205, 50, 0.15)';
      color = 'black';
  }

  return {
    backgroundColor,
    fontWeight: 700,
    color,
    fontSize: '12px',
    fontFamily: "'Lato', sans-serif",
    textAlign: 'center',
    border: '1px solid #c8e6c9',
    borderRadius: '4px',
    padding: '0px !important',
    height: '20px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '11px',
      padding: '0px !important',
      height: '28px',
    },
  };
});

export const ClosedQuarterFirstCell = styled(ClosedDataCell)(({ theme }) => ({
  backgroundColor: 'rgba(50, 205, 50, 0.15)',
  fontWeight: 700,
  color: 'black',
  fontSize: '12px',
  fontFamily: "'Lato', sans-serif",
  textAlign: 'center',
  border: '1px solid #c8e6c9',
  borderRadius: '4px',
  padding: '0px !important',
  height: '20px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '11px',
    padding: '0px !important',
    height: '28px',
  },
}));

// Button styled component from Table.styles.tsx
export const ActionButton = styled(Button)(({ theme }) => ({
  height: '28px',
  padding: '0 24px',
  background: '#E7EFF5',
  borderRadius: '9999px',
  fontSize: '12px',
  fontWeight: 700,
  fontFamily: "'Lato', sans-serif",
  textTransform: 'uppercase',
  color: '#1A1A1A',
  '&:hover': {
    background: '#d0e0ed',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '0 16px',
    fontSize: '11px',
    height: '24px',
    whiteSpace: 'nowrap',
  },
}));

// Helper functions
export const calculateTextWidth = (text: string, font: string): number => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return 0;
  
  context.font = font;
  const metrics = context.measureText(text);
  return metrics.width;
};

export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  
  return function(...args: Parameters<T>) {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};